import { Eye, Pause, Play, Trash2, Copy, RotateCcw } from "lucide-react"
import type { TableAction, BulkAction } from "@shared/types/data-table"
import type { NetworkToken } from "@shared/types/network-token.types"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"

interface NetworkTokenSpecificHandlers {
  onViewDetails?: (token: NetworkToken) => void
  onSuspend?: (token: NetworkToken) => void
  onResume?: (token: NetworkToken) => void
  onDelete?: (token: NetworkToken) => void
  onRefresh?: (token: NetworkToken) => void
}

export type NetworkTokenActionHandlers = BaseActionHandlers<NetworkToken> & NetworkTokenSpecificHandlers

const createNetworkTokenBulkActions = (): BulkAction[] => [
  {
    key: "suspend",
    label: "Suspend",
    icon: <Pause className="h-4 w-4" />,
    onClick: (_ids) => {},
  },
  {
    key: "delete",
    label: "Delete",
    icon: <Trash2 className="h-4 w-4" />,
    onClick: (_ids) => {},
  },
]

const createNetworkTokenRowActions = (handlers: NetworkTokenActionHandlers): TableAction<NetworkToken>[] => [
  {
    key: "view-details",
    label: "View details",
    icon: <Eye className="h-4 w-4" />,
    onClick: (token) => handlers.onViewDetails?.(token),
  },
  {
    key: "suspend",
    label: "Suspend token",
    icon: <Pause className="h-4 w-4" />,
    onClick: (token) => handlers.onSuspend?.(token),
    condition: (token) => token.status === "active",
  },
  {
    key: "resume",
    label: "Resume token",
    icon: <Play className="h-4 w-4" />,
    onClick: (token) => handlers.onResume?.(token),
    condition: (token) => token.status === "suspended",
  },
  {
    key: "refresh",
    label: "Refresh token",
    icon: <RotateCcw className="h-4 w-4" />,
    onClick: (token) => handlers.onRefresh?.(token),
    condition: (token) => token.status === "active",
  },
  {
    key: "delete",
    label: "Delete token",
    icon: <Trash2 className="h-4 w-4" />,
    onClick: (token) => handlers.onDelete?.(token),
    condition: (token) => token.status !== "deleted",
  },
  {
    key: "copy-id",
    label: "Copy token ID",
    icon: <Copy className="h-4 w-4" />,
    onClick: (token) => handlers.onCopyId?.(token),
  },
]

export const getNetworkTokenActions = (handlers: NetworkTokenActionHandlers = {}) => {
  const baseActions = createBaseActions<NetworkToken>('Network Token', handlers, {
    showEdit: false,
    showDelete: false,
    showFlag: false,
    showAddNote: false,
    showCopyId: false,
    showExport: true,
  })

  const specificActions = {
    bulkActions: createNetworkTokenBulkActions(),
    rowActions: createNetworkTokenRowActions(handlers),
  }

  return mergeActions(baseActions, specificActions)
}

export const getNetworkTokenBulkActions = (handlers: NetworkTokenActionHandlers = {}): BulkAction[] =>
  getNetworkTokenActions(handlers).bulkActions

export const getNetworkTokenRowActions = (_tokenData: NetworkToken, handlers: NetworkTokenActionHandlers = {}): TableAction<NetworkToken>[] =>
  getNetworkTokenActions(handlers).rowActions
